<template>
  <div class="card-body">
    <div class="row mb-3">
      <h3 class="mb-0 col-md-6">My Projects</h3>
    </div>
    <div class="row justify-content-between">
      <div class="my-1 col-md-4">
        Showing {{ fromItem }} to {{ toItem }} of
        {{ totalItems }}
        project<span v-if="totalItems === 0 || totalItems > 1">s</span>
      </div>

      <div class="col-md-3 my-1">
        <b-pagination
          v-if="totalItems > 0"
          :total-rows="totalItems"
          :per-page="perPage"
          v-model="currentPage"
          class="my-0 pagination-holder"
          prev-text="Prev"
          next-text="Next"
          align="fill"
        />
      </div>

      <div class="col-md-3 my-1">
        <b-form-group
          label="Per Page"
          label-align="right"
          label-cols-sm="7"
          label-cols-md="8"
          label-for="perPageSelect"
          class="mb-0"
        >
          <b-form-select
            v-model="perPage"
            id="perPageSelect"
            size="sm"
            :options="pageOptions"
            align="right"
          >
          </b-form-select>
        </b-form-group>
      </div>
    </div>

    <b-table
      show-empty
      stacked="md"
      id="projectsTable"
      ref="projectsTable"
      :filter="filterVal"
      :busy="isBusy"
      :items="projects"
      :fields="tableFields"
      :current-page="currentPage"
      :per-page="perPage"
      sort-icon-left
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-direction="sortDirection"
    >
      <template v-slot:cell(approvedAt)="row">
        <DateTime v-if="row.item.approvedAt" :date="row.item.approvedAt" :dateTime="false"></DateTime>
      </template>
      <template v-slot:cell(role)="row">
        <ProjectJoinButton :project=getProjectObject(row.item)></ProjectJoinButton>
      </template>
      <template v-slot:cell(options)="row">
        <b-btn
          class="mt-1 mr-1"
          :to="{ name: 'ProjectShow', params: { id: row.item.projectUrl } }"
          alt="View Project"
          title="View Project"
          target="_blank"
          noopener noreferrer
        >
          <font-awesome-icon icon="eye"/>
          View
        </b-btn>
      </template>
      <template #table-busy>
        <VueLoader style="height: 200px" text="Loading Projects..."/>
      </template>
    </b-table>

    <div class="card-footer">
      <div class="row justify-content-between">
        <div class="my-1 col-md-4">
          Showing {{ fromItem }} to {{ toItem }} of
          {{ totalItems }}
          project<span v-if="totalItems === 0 || totalItems > 1">s</span>
        </div>

        <div class="col-md-3 my-1">
          <b-pagination
            v-if="totalItems > 0"
            :total-rows="totalItems"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0 pagination-holder"
            prev-text="Prev"
            next-text="Next"
            align="fill"
          />
        </div>

        <div class="col-md-3 my-1">
          <b-form-group
            label="Per Page"
            label-align="right"
            label-cols-sm="7"
            label-cols-md="8"
            label-for="perPageSelect"
            class="mb-0"
          >
            <b-form-select
              v-model="perPage"
              id="perPageSelect"
              size="sm"
              :options="pageOptions"
              align="right"
            >
            </b-form-select>
          </b-form-group>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import VueLoader from "../../../layout/Loader";
import DateTime from "../../../../components/common/date-time/DateTime.vue";
import ProjectJoinButton from "../../../../assets/scripts/project/projectJoinButton.vue";

export default {
  name: "UserProjectsTable",
  components: {
    ProjectJoinButton,
    VueLoader,
    DateTime
  },
  data() {
    return {
      pageOptions: [10, 25, 50, 100, 250, 500],
      isBusy: false,

      tableFields: [
        {
          key: "approvedAt",
          label: "Date Joined",
          sortable: true,
          sortDirection: "asc",
          tdClass: "text-left",
        },
        {
          key: "projectName",
          label: "Project Name",
          sortable: true,
          tdClass: "text-dark",
          formatter: val => {
            return val;
          }
        },
        {
          key: "role",
          label: "Role",
          tdClass: "text-left",
          formatter: (val, fieldName, item) => {
            return val.charAt(0).toUpperCase() + val.slice(1);
          }
        },
      ]
    };
  },

  computed: {
    ...mapGetters({
      isLoading: 'user/projects/isLoading',
      projectPage: 'user/profile/projectPage',
      projectItems: 'user/profile/projectItems',
      projectTotal: 'user/profile/projectTotal',
      projectSearch: 'user/profile/projectSearch',
      projectSortBy: 'user/profile/projectSortBy',
      projectSortDirection: 'user/profile/projectSortDirection',
      projectSortDesc: 'user/profile/projectSortDesc',
    }),
    currentPage: {
      get() {
        return this.projectPage
      },
      set(newValue) {
        this.setProjectTablePage(newValue)
      }
    },
    totalItems: {
      get() {
        return this.projectTotal
      },
      set(newValue) {
        this.setProjectTableTotal(newValue)
      }
    },
    perPage: {
      get() {
        return this.projectItems
      },
      set(newValue) {
        this.setProjectTableItems(newValue)
      }
    },
    searchText: {
      get() {
        return this.projectSearch
      },
      set(newValue) {
        this.setProjectTableSearch(newValue)
      }
    },
    sortBy: {
      get() {
        return this.projectSortBy
      },
      set(newValue) {
        this.setProjectSortBy(newValue)
      }
    },
    sortDirection: {
      get() {
        return this.projectSortDirection
      },
      set(newValue) {
        this.setProjectSortDirection(newValue)
      }
    },
    sortDesc: {
      get() {
        return this.projectSortDesc
      },
      set(newValue) {
        this.setProjectSortDesc(newValue)
      }
    },

    fromItem() {
      return this.totalItems > 0
        ? (this.currentPage - 1) * this.perPage + 1
        : 0;
    },
    toItem() {
      return this.totalItems < this.currentPage * this.perPage
        ? this.totalItems
        : this.currentPage * this.perPage;
    },

    filterVal: {
      get: function () {
        // This is our multi-filter computed value
        // - search projects by location name
        //More filters can be added here
        // - also add an equivalent to the store

        let retVal = ''
        if (this.searchText) {
          retVal = retVal.length === 0 ? 'project.name=' + this.searchText : retVal + '&project.name=' + this.searchText
        }
        return retVal
      }
    },
  },
  methods: {
    ...mapActions({
      fetchProjects: 'user/projects/getProjects',
      setProjectTablePage: 'user/profile/setProjectTablePage',
      setProjectTableItems: 'user/profile/setProjectTableItems',
      setProjectTableTotal: 'user/profile/setProjectTableTotal',
      setProjectTableSearch: 'user/profile/setProjectTableSearch',
      setProjectSortBy: 'user/profile/setProjectTableSortBy',
      setProjectSortDirection: 'user/profile/setProjectTableSortDirection',
      setProjectSortDesc: 'user/profile/setProjectTableSortDesc',
    }),
    projects(ctx) {
      this.isBusy = true;
      if (ctx.sortBy === 'projectName') {
        ctx.sortBy = 'project.name'
      }
      let urlParams = `${this.urlBuilder(ctx)}`;
      const data = {id: this.projectId, urlParams};
      return this.fetchProjects(data).then(data => {
        this.totalItems = data["hydra:totalItems"];
        this.currentPage = ctx.currentPage || 1;
        this.sortBy = (ctx.sortBy === 'project.name' ? 'projectName' : ctx.sortBy) || "";
        this.isBusy = false;
        return data["hydra:member"].map(project => {
          return {
            ...project
          };
        });
      });
    },
    getProjectObject(project) {
      return {id: project.projectId, urlField: project.projectUrl, approveContacts: project.approveContacts}
    },
  }
};
</script>

<style scoped lang="scss">

</style>
